import React, { useState } from "react";
import { toast } from "sonner";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import InfoIcon from "@mui/icons-material/Info";
import { useNavigate } from "react-router-dom";
import { useCompanyProfile } from "../../../controllers/useCompanyProfile";
import { FleetrifyBgs } from "../../../assets/video";
import CarouselCard from "../../../components/Carousel";
import { fireSuccessConfetti } from "../../../utils/confetti";
import { KycNameField } from "../../../components/KycNameField";

interface Section {
    key: string;
    label: string;
    required?: boolean;
    description?: string;
}

const CompanyKYCForm = () => {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [showPersonalIDDropdown, setShowPersonalIDDropdown] = useState(false);

    const { submitProfile, isSubmitting } = useCompanyProfile();
    const [kycValues, setKycValues] = useState<Record<string, string>>({
        nin: "",
        //passport: "",
        driversLicense: "",
        voterCard: "",
        businessReg: "",
        tin: "",
    });

    const [modalVisible, setModalVisible] = useState(false);

    const slides = [
        {
            title: "Welcome to Fleetrify",
            description: "Login to manage logistics and fleet operations",
        },
        {
            title: "Real-time Fleet Insights",
            description: "Get live updates and analytics on every vehicle in your fleet",
        },
        {
            title: "Simplify Logistics Management",
            description: "Automate routing, tracking, and reporting — all in one platform",
        },
    ];

    const toggleSection = (key: string) => {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleInputChange = (key: string, value: string) => {
        setKycValues((prev) => ({ ...prev, [key]: value }));
    };

    const personalIDSections: Section[] = [
        { key: "nin", label: "NIN", description: "Enter your National Identification Number" },
        //{ key: "passport", label: "Passport", description: "Enter your Passport number" },
        { key: "driversLicense", label: "Drivers License", description: "Enter your Drivers License number" },
        { key: "voterCard", label: "Voter Card", description: "Enter your Voter Card number" },
    ];

    const businessIDSections: Section[] = [
        { key: "businessReg", label: "Business Registration Number", required: true },
        { key: "tin", label: "Tax Identification Number" },
    ];

    const renderSection = (section: Section) => {
        const isOpen = expanded[section.key];
        return (
            <div key={section.key}>
                <button
                    type="button"
                    onClick={() => toggleSection(section.key)}
                    className={`w-full px-4 py-3 rounded-md flex items-center justify-between text-left mt-2 transition duration-200 ${isOpen ? "bg-[#143887] text-white border border-blue-300 shadow-sm" : "bg-gray-100"
                        }`}
                >
                    <span>
                        {section.label}
                        {section.required && <span className="text-red-500 ml-1">*</span>}
                    </span>
                    {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>

                {isOpen && (
                    <div className="p-4">
                        {section.description && (
                            <p className="text-sm text-gray-700 mb-2">{section.description}</p>
                        )}
                        <input
                            type="text"
                            placeholder={`Enter ${section.label}`}
                            value={kycValues[section.key] || ""}
                            onChange={(e) => handleInputChange(section.key, e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm text-gray-600"
                        />
                    </div>
                )}
            </div>
        );
    };

    const handleSubmit = async () => {
        try {
            await submitProfile(kycValues);
            setModalVisible(true);
            fireSuccessConfetti();
        } catch (error) {
            console.error(error);
            toast.error("❌ Error submitting KYC. Check console for details.");
        }
    };

    const handleModalClose = () => {
        setModalVisible(false);
        navigate("/login"); 
    };

    return (
        <div className="relative min-h-screen lg:flex">
            {/* video section */}
            <div className="relative w-full lg:w-[45%] h-screen lg:sticky top-0 flex-shrink-0">
                <video
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                >
                    <source src={FleetrifyBgs} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="absolute inset-0 bg-black opacity-50 z-10"></div>
                <div>
                    <CarouselCard slides={slides} />
                </div>
            </div>

            {/*form section */}
            <div className="w-full lg:w-[55%] flex items-center justify-center lg:static absolute inset-0 z-30 px-2 sm:px-4 lg:px-6 overflow-y-auto">
                <div className="w-full md:w-[65%] p-6 bg-white rounded-lg shadow lg:shadow-none lg:w-[75%] xl:w-[75%] 2xl:w-[75%] max max-w-[850px] space-y-6 lg:rounded-none">

                    <h2 className="text-center text-2xl font-bold text-blue-900">Compliance and KYC Check</h2>
                    <p className="text-center text-sm text-gray-700">
                        Kindly provide your identification information below.
                    </p>

                    {/* full name */}
                    <KycNameField label="Representative's name on file" idLabel="personal ID document" />

                    {/* personal id verification */}
                    <div className="border rounded-lg p-4 space-y-2">
                        <div className="flex items-center gap-2 mb-2 text-blue-900 font-semibold">
                            <PersonIcon />
                            <span>Personal ID Verification</span>
                        </div>

                        <button
                            type="button"
                            onClick={() => setShowPersonalIDDropdown(!showPersonalIDDropdown)}
                            className="w-full bg-blue-50 px-4 py-3 rounded-md flex items-center justify-between text-left"
                        >
                            <span>Select an ID</span>
                            {showPersonalIDDropdown ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </button>

                        {showPersonalIDDropdown && (
                            <div className="space-y-2">
                                {personalIDSections.map(renderSection)}
                            </div>
                        )}
                    </div>

                    {/* business ic verification */}
                    <div className="border rounded-lg p-4 space-y-2">
                        <div className="flex items-center gap-2 mb-2 text-blue-900 font-semibold">
                            <BusinessIcon />
                            <span>Business ID Verification</span>
                        </div>
                        {businessIDSections.map(renderSection)}
                    </div>

                    {/* info box */}
                    <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-md p-4 flex items-start gap-2">
                        <InfoIcon className="mt-0.5" />
                        <div>
                            <span className="font-semibold">Info!</span>
                            <p>You are expected to provide at least one personal ID to verify.</p>
                        </div>
                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="w-full bg-[#143887] text-white py-3 rounded-md hover:bg-blue-800 text-sm font-medium"
                    >
                        {isSubmitting ? "Submitting..." : "Done"}
                    </button>
                </div>
            </div>

            {/*  success modal */}
            {modalVisible && (
                <div className="fixed inset-0 flex items-center justify-center z-50">
                    {/* overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>

                    <div className="relative bg-white rounded-2xl p-8 w-96 max-w-sm shadow-2xl flex flex-col items-center animate-fadeIn">
                        <div className="bg-green-100 rounded-full p-4 mb-4">
                            <svg
                                className="w-12 h-12 text-green-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">Success!</h2>
                        <p className="text-gray-600 text-center mb-6">
                            Your company profile and KYC have been successfully submitted and approved.
                        </p>

                        {/*  button */}
                        <button
                            onClick={handleModalClose}
                            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-full transition-transform transform hover:scale-105 shadow-md"
                        >
                            Okay
                        </button>
                    </div>
                </div>

            )}
        </div>
    );
};

export default CompanyKYCForm;
